import React, { useEffect, useRef, useState } from 'react';

const RED    = '#bf1e2e';
const NAVY   = '#014c8d';
const ORG    = '#E84519';
const BG     = '#080d16';
const SURFACE  = '#0d1525';
const BORDER   = 'rgba(255,255,255,0.08)';
const DIM      = 'rgba(255,255,255,0.50)';
const DESIGN_W = 960;
const DESIGN_H = DESIGN_W * 9 / 16;
const PASSWORD = 'monarchs2026';

type SlideId =
  | 'cover' | 'opportunity' | 'whyearly' | 'program'
  | 'howitworks' | 'privacy' | 'proposal' | 'pricing' | 'nextsteps';

const SLIDES: Array<{ id: SlideId; label: string }> = [
  { id: 'cover',       label: '01  Cover' },
  { id: 'opportunity', label: '02  Opportunity' },
  { id: 'whyearly',   label: '03  Why It Starts Early' },
  { id: 'program',    label: '04  Early Pathways' },
  { id: 'howitworks', label: '05  How It Works' },
  { id: 'privacy',    label: '06  Data and Privacy' },
  { id: 'proposal',   label: '07  Partnership Proposal' },
  { id: 'pricing',    label: '08  Pricing' },
  { id: 'nextsteps',  label: '09  Next Steps' },
];

// ─── ScaledSlide ──────────────────────────────────────────────────────────────
function ScaledSlide({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setScale(el.offsetWidth / DESIGN_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ width: '100%', height: DESIGN_H * scale, position: 'relative' }}>
      <div style={{
        width: DESIGN_W, height: DESIGN_H,
        transform: `scale(${scale})`, transformOrigin: 'top left',
        position: 'absolute', top: 0, left: 0,
      }}>
        {children}
      </div>
    </div>
  );
}

// ─── SlideShell ───────────────────────────────────────────────────────────────
function SlideShell({ tag, children, center = false }: {
  tag?: string; children: React.ReactNode; center?: boolean;
}) {
  return (
    <div style={{
      width: '100%', height: '100%', background: BG,
      display: 'flex', flexDirection: 'column',
      justifyContent: center ? 'center' : 'flex-start',
      alignItems: center ? 'center' : 'stretch',
      padding: '48px 64px', position: 'relative',
      overflow: 'hidden', boxSizing: 'border-box',
    }}>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
      }} />
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 4,
        background: `linear-gradient(to right, ${RED} 0%, ${RED} 45%, ${ORG} 55%, ${ORG} 100%)`,
      }} />
      {tag && (
        <div style={{
          position: 'absolute', top: 18, left: 64,
          fontSize: 9, fontWeight: 800, letterSpacing: 3.5,
          color: ORG, textTransform: 'uppercase',
          fontFamily: "'Barlow Condensed', sans-serif",
        }}>{tag}</div>
      )}
      <div style={{
        position: 'absolute', bottom: 10, left: 64, right: 48,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <img
          src="/bthd-email-sig-300x80-transparent.png"
          alt="BTHD"
          style={{ height: 13, width: 'auto', opacity: 0.48 }}
        />
        <div style={{ width: 1, height: 11, background: 'rgba(255,255,255,0.14)' }} />
        <span style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.35)', letterSpacing: 0.3 }}>
          Bigger Than Hoop Dreams Inc.
        </span>
      </div>
      {children}
    </div>
  );
}

// ─── Shared components ────────────────────────────────────────────────────────
function Pill({ children, orange = false }: { children: React.ReactNode; orange?: boolean }) {
  const c = orange ? ORG : RED;
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center',
      background: `${c}1a`, border: `1px solid ${c}44`,
      borderRadius: 4, padding: '3px 10px',
      fontSize: 9, fontWeight: 800, letterSpacing: 2.5,
      color: c, textTransform: 'uppercase', marginBottom: 14,
      fontFamily: "'Barlow Condensed', sans-serif",
    }}>{children}</div>
  );
}

function H({ size = 46, children }: { size?: number; children: React.ReactNode }) {
  return (
    <h2 style={{
      margin: '0 0 8px', fontSize: size, fontWeight: 900,
      lineHeight: 1.0, color: '#fff', textTransform: 'uppercase',
      letterSpacing: -0.5, fontFamily: "'Barlow Condensed', sans-serif",
    }}>{children}</h2>
  );
}

const O = ({ children }: { children: React.ReactNode }) =>
  <span style={{ color: ORG }}>{children}</span>;

function MonarchsLogo({ height = 44 }: { height?: number }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 7, padding: '3px 8px',
      display: 'inline-flex', alignItems: 'center', flexShrink: 0,
    }}>
      <img src="/monarchs-logo.png" alt="Monarchs Basketball"
        style={{ height, width: 'auto', display: 'block' }} />
    </div>
  );
}

// ─── Slide 01: Cover ─────────────────────────────────────────────────────────
function CoverSlide() {
  return (
    <SlideShell center>
      <div style={{
        position: 'absolute', top: '38%', left: '28%', transform: 'translate(-50%,-50%)',
        width: 380, height: 280,
        background: `radial-gradient(ellipse, ${RED}1e 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', top: '38%', left: '72%', transform: 'translate(-50%,-50%)',
        width: 380, height: 280,
        background: `radial-gradient(ellipse, ${ORG}1e 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 28, marginBottom: 36,
        }}>
          <img
            src="/bthd-email-sig-300x80-transparent.png"
            alt="Bigger Than Hoop Dreams"
            style={{ height: 36, width: 'auto' }}
          />
          <div style={{ width: 1, height: 48, background: 'rgba(255,255,255,0.16)' }} />
          <MonarchsLogo height={42} />
        </div>
        <div style={{
          fontSize: 10, fontWeight: 800, letterSpacing: 4,
          color: ORG, textTransform: 'uppercase', marginBottom: 18,
          fontFamily: "'Barlow Condensed', sans-serif",
        }}>Early Pathways Partnership Proposal</div>
        <h1 style={{
          fontSize: 44, fontWeight: 900, lineHeight: 1.1,
          color: '#fff', textTransform: 'uppercase',
          letterSpacing: -0.5, margin: '0 auto',
          fontFamily: "'Barlow Condensed', sans-serif",
          maxWidth: 720,
        }}>
          Where young athletes build character, skills,<br />
          and their <span style={{ color: ORG }}>Name, Image and Likeness.</span>
        </h1>
      </div>
    </SlideShell>
  );
}

// ─── Slide 02: Opportunity ────────────────────────────────────────────────────
function OpportunitySlide() {
  return (
    <SlideShell>
      <div style={{ marginTop: 28 }}>
        <Pill>Partnership Overview</Pill>
        <H size={44}>Two Programs.<br /><O>One Complete Development Story.</O></H>
        <div style={{ display: 'flex', gap: 20, marginTop: 24 }}>
          <div style={{
            flex: 1, background: `${ORG}0e`,
            border: `1px solid ${ORG}33`, borderTop: `3px solid ${ORG}`,
            borderRadius: 10, padding: '20px 22px',
          }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: 2.5,
              color: ORG, textTransform: 'uppercase', marginBottom: 8,
              fontFamily: "'Barlow Condensed', sans-serif",
            }}>Monarchs</div>
            <div style={{
              fontSize: 19, fontWeight: 900, color: '#fff',
              textTransform: 'uppercase', lineHeight: 1.1, marginBottom: 8,
              fontFamily: "'Barlow Condensed', sans-serif",
            }}>Develops Athletes</div>
            <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.65 }}>
              World class basketball development from youth through Varsity.
            </div>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 6, flexShrink: 0, width: 32,
          }}>
            <div style={{ width: 1, flex: 1, background: 'rgba(255,255,255,0.08)' }} />
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: SURFACE, border: `1px solid ${BORDER}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 900, color: 'rgba(255,255,255,0.25)',
            }}>+</div>
            <div style={{ width: 1, flex: 1, background: 'rgba(255,255,255,0.08)' }} />
          </div>
          <div style={{
            flex: 1, background: `${RED}0e`,
            border: `1px solid ${RED}33`, borderTop: `3px solid ${RED}`,
            borderRadius: 10, padding: '20px 22px',
          }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: 2.5,
              color: RED, textTransform: 'uppercase', marginBottom: 8,
              fontFamily: "'Barlow Condensed', sans-serif",
            }}>BTHD</div>
            <div style={{
              fontSize: 19, fontWeight: 900, color: '#fff',
              textTransform: 'uppercase', lineHeight: 1.1, marginBottom: 10,
              fontFamily: "'Barlow Condensed', sans-serif",
            }}>Develops What They Carry Beyond the Court</div>
            <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.65 }}>
              Identity, character, NIL readiness, and the habits that define who they are becoming alongside the game.
            </div>
          </div>
        </div>
        <div style={{
          marginTop: 18, padding: '14px 22px',
          background: SURFACE, border: `1px solid ${BORDER}`,
          borderRadius: 8, textAlign: 'center',
        }}>
          <span style={{
            fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.82)',
            fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.3,
          }}>
            Monarchs develops athletes. BTHD develops who those athletes are becoming.
            Two programs, one complete development story.
          </span>
        </div>
      </div>
    </SlideShell>
  );
}

// ─── Slide 03: Why It Starts Early ───────────────────────────────────────────
function WhyEarlySlide() {
  return (
    <SlideShell>
      <div style={{ marginTop: 28, display: 'flex', gap: 44, alignItems: 'flex-start' }}>
        <div style={{ flex: '0 0 300px' }}>
          <Pill orange>Why It Starts Early</Pill>
          <H size={44}>The Work Starts<br /><O>When the Game Does.</O></H>
          <div style={{
            width: 40, height: 3, borderRadius: 2, margin: '16px 0',
            background: `linear-gradient(to right, ${RED}, ${ORG})`,
          }} />
          <p style={{ fontSize: 13, color: DIM, lineHeight: 1.7, margin: 0 }}>
            NIL, recruiting, scholarships, sponsorships. That work should
            start at the same age the game does, not the day it becomes urgent.
          </p>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', paddingTop: 16 }}>
          <div style={{
            background: `${ORG}0c`, border: `1px solid ${ORG}28`,
            borderLeft: `4px solid ${ORG}`, borderRadius: 10, padding: '28px 30px',
          }}>
            <div style={{
              fontSize: 64, fontWeight: 900, color: `${ORG}30`,
              lineHeight: 0.7, marginBottom: 14,
              fontFamily: "'Barlow Condensed', sans-serif",
            }}>"</div>
            <p style={{
              fontSize: 18, fontWeight: 700, color: 'rgba(255,255,255,0.90)',
              lineHeight: 1.60, margin: '0 0 14px',
            }}>
              Every Monarchs athlete will eventually face a moment where their
              name, their story, and how they carry themselves matters as much as their game.
            </p>
            <p style={{ fontSize: 13, color: DIM, lineHeight: 1.65, margin: 0 }}>
              BTHD Early Pathways makes sure they are ready for it.
            </p>
          </div>
        </div>
      </div>
    </SlideShell>
  );
}

// ─── Slide 04: BTHD Early Pathways ───────────────────────────────────────────
function ProgramSlide() {
  const stages = [
    { name: 'Development',   bthd: 'Athletes begin building identity vocabulary and character habits alongside their first competitive experience.' },
    { name: 'Rising Stars',  bthd: 'Self awareness deepens. Athletes begin to understand who they are beyond the court and why it matters.' },
    { name: 'City League',   bthd: 'Athletes take independent ownership of their profile and NIL awareness.' },
    { name: 'Varsity League', bthd: 'By Varsity age, the habits, character, and profile are already in place.' },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 24 }}>
        <Pill>BTHD Early Pathways</Pill>
        <H size={42}>Runs Alongside<br /><O>Every Monarchs Stage.</O></H>
        <p style={{ color: DIM, fontSize: 13, marginBottom: 18, maxWidth: 680, lineHeight: 1.65 }}>
          BTHD Early Pathways is a web based, staged identity and character development program that runs alongside an athlete's basketball journey from their earliest stages, giving athletes the habits, profile, and self awareness they need before NIL becomes relevant.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          {stages.map(({ name, bthd }, i) => (
            <div key={name} style={{
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderTop: `3px solid ${i < 2 ? ORG : RED}`,
              borderRadius: 10, padding: '16px 15px 14px',
            }}>
              <div style={{
                fontSize: 9, fontWeight: 800, letterSpacing: 2,
                color: i < 2 ? ORG : RED, textTransform: 'uppercase',
                marginBottom: 5, fontFamily: "'Barlow Condensed', sans-serif",
              }}>Stage {i + 1}</div>
              <div style={{
                fontSize: 15, fontWeight: 900, color: '#fff',
                textTransform: 'uppercase', lineHeight: 1.1, marginBottom: 10,
                fontFamily: "'Barlow Condensed', sans-serif",
              }}>{name}</div>
              <div style={{ fontSize: 11.5, color: DIM, lineHeight: 1.55 }}>{bthd}</div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

// ─── Slide 05: How It Works ───────────────────────────────────────────────────
function HowItWorksSlide() {
  const steps = [
    {
      stage: 'Entry',
      title: 'Monarchs Code',
      body: 'Athletes sign in with a Monarchs code.',
      color: ORG,
    },
    {
      stage: 'Development and Rising Stars',
      title: 'Parents and Athletes Together',
      body: 'At younger ages, parents and athletes move through the program together. Consent, shared progress, and family level engagement built in.',
      color: `#f0864a`,
    },
    {
      stage: 'City League',
      title: 'Athletes Take Ownership',
      body: 'As athletes age up, they own the work independently. Content adapts to their growing agency and awareness.',
      color: RED,
    },
    {
      stage: 'Varsity League',
      title: 'Profile Already Built',
      body: 'By Varsity age, the habits, character, and profile are already in place.',
      color: NAVY,
    },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 24 }}>
        <Pill orange>Progression Model</Pill>
        <H size={40}>From First Practice<br /><O>to Varsity Ready.</O></H>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginTop: 18 }}>
          {steps.map(({ stage, title, body, color }) => (
            <div key={stage} style={{
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderTop: `3px solid ${color}`,
              borderRadius: 10, padding: '16px 15px 14px',
            }}>
              <div style={{
                fontSize: 9, fontWeight: 800, letterSpacing: 1.5,
                color, textTransform: 'uppercase', marginBottom: 6,
                fontFamily: "'Barlow Condensed', sans-serif",
              }}>{stage}</div>
              <div style={{
                fontSize: 15, fontWeight: 900, color: '#fff',
                textTransform: 'uppercase', lineHeight: 1.1, marginBottom: 10,
                fontFamily: "'Barlow Condensed', sans-serif",
              }}>{title}</div>
              <div style={{ fontSize: 11.5, color: DIM, lineHeight: 1.55 }}>{body}</div>
            </div>
          ))}
        </div>
        <div style={{
          marginTop: 14, padding: '11px 20px',
          background: SURFACE, border: `1px solid ${BORDER}`,
          borderRadius: 8, textAlign: 'center',
        }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.50)', letterSpacing: 0.3 }}>
            Web based. No app download required. Accessible on any device.
          </span>
        </div>
      </div>
    </SlideShell>
  );
}

// ─── Slide 06: Data and Privacy ───────────────────────────────────────────────
function PrivacySlide() {
  return (
    <SlideShell center>
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: 500, height: 350,
        background: `radial-gradient(ellipse, ${NAVY}22 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <svg width="52" height="60" viewBox="0 0 52 60" fill="none" style={{ marginBottom: 18 }}>
          <path d="M26 2L2 11V33C2 46 12 57 26 60C40 57 50 46 50 33V11L26 2Z"
            fill={`${NAVY}3a`} stroke={`${NAVY}88`} strokeWidth="1.5" />
          <path d="M26 8L6 15.5V33C6 44 13.5 53.5 26 56.5C38.5 53.5 46 44 46 33V15.5L26 8Z"
            fill={`${ORG}12`} stroke={`${ORG}55`} strokeWidth="1.5" />
          <path d="M18 30L23 35L34 24" stroke={ORG} strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <Pill>Data and Privacy</Pill>
        <h1 style={{
          fontSize: 64, fontWeight: 900, color: '#fff',
          textTransform: 'uppercase', letterSpacing: -1, lineHeight: 1.0,
          margin: '8px 0 12px', fontFamily: "'Barlow Condensed', sans-serif",
        }}>
          Name and email only.
        </h1>
        <div style={{
          fontSize: 24, fontWeight: 700, color: ORG,
          textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 28,
          fontFamily: "'Barlow Condensed', sans-serif",
        }}>Nothing else collected.</div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          {['No biometric data', 'No location tracking', 'No third-party sharing', 'No behavioral profiling'].map(item => (
            <div key={item} style={{
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 20, padding: '6px 14px',
              fontSize: 11, fontWeight: 600, color: DIM, letterSpacing: 0.3,
            }}>{item}</div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

// ─── Slide 07: Partnership Proposal ──────────────────────────────────────────
function ProposalSlide() {
  return (
    <SlideShell>
      <div style={{ marginTop: 28, display: 'flex', gap: 40, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <Pill orange>Partnership Opportunity</Pill>
          <H size={44}>The Organization<br /><O>That Builds the Model.</O></H>
          <p style={{ color: 'rgba(255,255,255,0.82)', fontSize: 14, lineHeight: 1.7, marginTop: 14, marginBottom: 10 }}>
            We are inviting Monarchs to be the founding partner of BTHD Early Pathways.
          </p>
          <p style={{ color: DIM, fontSize: 13.5, lineHeight: 1.7, marginBottom: 20 }}>
            This partnership becomes the model we bring to organizations across
            Canada and the United States. Monarchs sets the standard.
          </p>
          <div style={{
            background: `${ORG}0e`, border: `1px solid ${ORG}28`,
            borderLeft: `3px solid ${ORG}`, borderRadius: 8, padding: '14px 18px',
          }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: 2.5, color: ORG,
              textTransform: 'uppercase', marginBottom: 6,
              fontFamily: "'Barlow Condensed', sans-serif",
            }}>Founding Partner Designation</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.82)', lineHeight: 1.6 }}>
              Monarchs is recognized as the first club to build athlete character
              development alongside athletic development from day one.
            </div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 11 }}>
          {[
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                  <path d="M5 2h8M5 2C5 2 3 2 3 5s2 3 2 3h8c0 0 2 0 2-3s-2-3-2-3" stroke={ORG} strokeWidth="1.4" strokeLinejoin="round" />
                  <line x1="9" y1="8" x2="9" y2="12" stroke={ORG} strokeWidth="1.4" strokeLinecap="round" />
                  <path d="M6.5 12h5" stroke={ORG} strokeWidth="1.4" strokeLinecap="round" />
                  <path d="M7 15h4" stroke={ORG} strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              ),
              title: 'Founding Partner Recognition',
              body: 'Publicly recognized as the organization that launched BTHD Early Pathways in Canada.',
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                  <rect x="3" y="8" width="12" height="8" rx="2" stroke={ORG} strokeWidth="1.4" />
                  <path d="M6 8V6a3 3 0 0 1 6 0v2" stroke={ORG} strokeWidth="1.4" strokeLinecap="round" />
                  <circle cx="9" cy="12" r="1" fill={ORG} />
                </svg>
              ),
              title: 'First Right of Renewal',
              body: 'Locked-in founding partner rate and first right of renewal before any other club in your market.',
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                  <path d="M2 15V10" stroke={ORG} strokeWidth="1.4" strokeLinecap="round" />
                  <path d="M7 15V6" stroke={ORG} strokeWidth="1.4" strokeLinecap="round" />
                  <path d="M12 15V9" stroke={ORG} strokeWidth="1.4" strokeLinecap="round" />
                  <path d="M1 15h16" stroke={ORG} strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              ),
              title: 'Annual Impact Report',
              body: 'Year-end data showing athlete progress, program completion, and cohort outcomes.',
            },
            {
              icon: (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                  <circle cx="6" cy="5" r="2.5" stroke={ORG} strokeWidth="1.4" />
                  <circle cx="12" cy="5" r="2.5" stroke={ORG} strokeWidth="1.4" />
                  <path d="M1 15c0-2.76 2.24-5 5-5h6c2.76 0 5 2.24 5 5" stroke={ORG} strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              ),
              title: 'Direct Collaboration',
              body: 'Shape the program alongside BTHD as it expands. Your feedback informs what comes next.',
            },
          ].map(({ icon, title, body }) => (
            <div key={title} style={{
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 10, padding: '12px 16px',
              display: 'flex', gap: 14, alignItems: 'flex-start',
            }}>
              {icon}
              <div>
                <div style={{
                  fontSize: 13, fontWeight: 800, color: '#fff', marginBottom: 3,
                  fontFamily: "'Barlow Condensed', sans-serif",
                }}>{title}</div>
                <div style={{ fontSize: 11.5, color: DIM, lineHeight: 1.55 }}>{body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

// ─── Slide 08: Pricing ────────────────────────────────────────────────────────
function PricingSlide() {
  const tiers = [
    {
      name: 'Tier 1',
      base: '$10,000',
      perAthlete: 'plus $5 per athlete per year',
      featured: false,
      tagline: 'Full platform access',
      features: [
        'All four Early Pathways stages',
        'Monarchs-branded athlete access code',
        'Parent and athlete co-entry at early ages',
        'Annual platform license',
      ],
    },
    {
      name: 'Tier 2',
      base: '$15,000',
      perAthlete: 'plus $5 per athlete per year',
      featured: true,
      tagline: 'Admin dashboard included',
      features: [
        'Everything in Tier 1',
        'Organization admin dashboard',
        'Athlete roster and progress tracking',
        'Cohort completion reporting',
      ],
    },
    {
      name: 'Tier 3',
      base: '$22,500',
      perAthlete: 'plus $5 per athlete per year',
      featured: false,
      tagline: 'In-person programming included',
      features: [
        'Everything in Tier 2',
        'In-person engagement sessions',
        'Camps, events, and panels',
        'Two annual touchpoints included',
      ],
    },
  ];
  return (
    <SlideShell>
      <div style={{ marginTop: 24 }}>
        <Pill>Annual License</Pill>
        <H size={42}>Simple, transparent pricing.<br /><O>Built for organizations of every size.</O></H>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginTop: 14 }}>
          {tiers.map(({ name, base, perAthlete, featured, tagline, features }) => (
            <div key={name} style={{
              background: featured ? `${ORG}0d` : SURFACE,
              border: `1px solid ${featured ? ORG : BORDER}`,
              borderTop: `3px solid ${featured ? ORG : RED}`,
              borderRadius: 10, overflow: 'hidden',
            }}>
              <div style={{
                padding: '12px 16px 10px',
                borderBottom: `1px solid ${featured ? `${ORG}30` : BORDER}`,
                background: featured ? `${ORG}08` : 'transparent',
              }}>
                {featured && (
                  <div style={{
                    fontSize: 8, fontWeight: 800, letterSpacing: 1.5, color: ORG,
                    textTransform: 'uppercase', marginBottom: 3,
                    fontFamily: "'Barlow Condensed', sans-serif",
                  }}>Recommended</div>
                )}
                <div style={{
                  fontSize: 18, fontWeight: 900, color: '#fff',
                  textTransform: 'uppercase', fontFamily: "'Barlow Condensed', sans-serif",
                }}>{name}</div>
                <div style={{ fontSize: 10, color: DIM, marginTop: 2 }}>{tagline}</div>
              </div>
              <div style={{ padding: '10px 16px 8px', borderBottom: `1px solid ${BORDER}` }}>
                <div style={{
                  fontSize: 34, fontWeight: 900, color: featured ? ORG : '#fff', lineHeight: 1,
                  fontFamily: "'Barlow Condensed', sans-serif",
                }}>{base}</div>
                <div style={{ fontSize: 9, color: DIM, letterSpacing: 0.5, marginTop: 2 }}>CAD PER YEAR</div>
                <div style={{
                  fontSize: 10, marginTop: 4,
                  color: featured ? `${ORG}bb` : 'rgba(255,255,255,0.40)',
                }}>{perAthlete}</div>
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: '12px 16px 14px' }}>
                {features.map(f => (
                  <li key={f} style={{
                    fontSize: 11.5, color: 'rgba(255,255,255,0.78)',
                    padding: '3px 0 3px 16px', position: 'relative', lineHeight: 1.45,
                  }}>
                    <span style={{
                      position: 'absolute', left: 0,
                      color: featured ? ORG : RED, fontWeight: 700, fontSize: 10,
                    }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: DIM, fontStyle: 'italic', marginTop: 10 }}>
          All pricing in CAD. Multi-year founding partner rates available. Contact harry@bthd.app to discuss.
        </div>
      </div>
    </SlideShell>
  );
}

// ─── Slide 09: Next Steps ─────────────────────────────────────────────────────
function NextStepsSlide() {
  const steps = [
    'Introductory conversation, align on cohort size and timing',
    'Launch founding partner cohort through BTHD Early Pathways',
    'Evaluate together and shape what comes next',
  ];
  return (
    <SlideShell center>
      <div style={{
        position: 'absolute', top: '42%', left: '50%', transform: 'translate(-50%,-50%)',
        width: 600, height: 320,
        background: `radial-gradient(ellipse, ${ORG}14 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1, maxWidth: 680, margin: '0 auto' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 24, marginBottom: 28,
        }}>
          <img
            src="/bthd-email-sig-300x80-transparent.png"
            alt="BTHD"
            style={{ height: 30, width: 'auto' }}
          />
          <div style={{ width: 1, height: 36, background: 'rgba(255,255,255,0.16)' }} />
          <MonarchsLogo height={34} />
        </div>
        <div style={{
          fontSize: 10, fontWeight: 800, letterSpacing: 4, color: ORG,
          textTransform: 'uppercase', marginBottom: 14,
          fontFamily: "'Barlow Condensed', sans-serif",
        }}>Next Steps</div>
        <h2 style={{
          fontSize: 44, fontWeight: 900, color: '#fff',
          textTransform: 'uppercase', letterSpacing: -0.5,
          lineHeight: 1.06, margin: '0 0 24px',
          fontFamily: "'Barlow Condensed', sans-serif",
        }}>
          Every great athlete has a foundation.<br />
          <span style={{ color: ORG }}>Let's build theirs together.</span>
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 26 }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 8, padding: '10px 16px', textAlign: 'left',
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: `${ORG}1e`, border: `1px solid ${ORG}55`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <span style={{ fontSize: 11, fontWeight: 900, color: ORG }}>{i + 1}</span>
              </div>
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.82)', lineHeight: 1.4 }}>{s}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 14 }}>
          <a href="mailto:harry@bthd.app" style={{
            display: 'inline-block', padding: '11px 26px',
            background: ORG, border: 'none',
            borderRadius: 6, fontSize: 13, fontWeight: 700,
            color: '#fff', textDecoration: 'none', letterSpacing: 0.5,
          }}>harry@bthd.app</a>
          <a href="https://bthd.app" target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-block', padding: '11px 26px',
            background: 'transparent', border: `1px solid ${BORDER}`,
            borderRadius: 6, fontSize: 13, fontWeight: 700,
            color: 'rgba(255,255,255,0.55)', textDecoration: 'none', letterSpacing: 0.5,
          }}>bthd.app</a>
        </div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)', letterSpacing: 0.5 }}>
          Bigger Than Hoop Dreams Inc. · Kitchener, Ontario, Canada · bthd.app
        </div>
      </div>
    </SlideShell>
  );
}

// ─── Renderer ─────────────────────────────────────────────────────────────────
function renderSlide(id: SlideId): React.ReactNode {
  switch (id) {
    case 'cover':       return <CoverSlide />;
    case 'opportunity': return <OpportunitySlide />;
    case 'whyearly':   return <WhyEarlySlide />;
    case 'program':    return <ProgramSlide />;
    case 'howitworks': return <HowItWorksSlide />;
    case 'privacy':    return <PrivacySlide />;
    case 'proposal':   return <ProposalSlide />;
    case 'pricing':    return <PricingSlide />;
    case 'nextsteps':  return <NextStepsSlide />;
  }
}

// ─── Password Gate ────────────────────────────────────────────────────────────
function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const attempt = () => {
    if (value.trim() === PASSWORD) {
      onUnlock();
    } else {
      setError(true);
      setValue('');
      setTimeout(() => setError(false), 1400);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: BG,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Barlow Condensed', 'Barlow', sans-serif",
    }}>
      <div style={{
        width: 360, textAlign: 'center',
        background: SURFACE, border: `1px solid ${BORDER}`,
        borderTop: `3px solid ${ORG}`, borderRadius: 12,
        padding: '40px 36px',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 18, marginBottom: 20,
        }}>
          <img
            src="/bthd-email-sig-300x80-transparent.png"
            alt="BTHD"
            style={{ height: 28, width: 'auto' }}
          />
          <div style={{ width: 1, height: 34, background: 'rgba(255,255,255,0.16)' }} />
          <MonarchsLogo height={30} />
        </div>
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: 3, color: ORG,
          textTransform: 'uppercase', marginBottom: 6,
        }}>Confidential</div>
        <div style={{
          fontSize: 20, fontWeight: 900, color: '#fff',
          marginBottom: 24, textTransform: 'uppercase', lineHeight: 1.2,
        }}>
          BTHD × Monarchs<br />Partnership Deck
        </div>
        <input
          type="password"
          value={value}
          placeholder="Enter access code"
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && attempt()}
          style={{
            width: '100%', padding: '11px 14px',
            background: BG, border: `1px solid ${error ? ORG : BORDER}`,
            borderRadius: 6, color: '#fff', fontSize: 14,
            outline: 'none', boxSizing: 'border-box',
            transition: 'border-color 0.2s',
            fontFamily: 'inherit',
          }}
        />
        {error && (
          <div style={{ fontSize: 12, color: ORG, marginTop: 8 }}>Incorrect access code.</div>
        )}
        <button
          onClick={attempt}
          style={{
            marginTop: 14, width: '100%', padding: '11px',
            background: ORG, border: 'none', borderRadius: 6,
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', letterSpacing: 0.5, fontFamily: 'inherit',
          }}
        >
          Access Deck
        </button>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.22)', marginTop: 20, lineHeight: 1.7 }}>
          Bigger Than Hoop Dreams Inc.<br />
          For review purposes only · Confidential
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function MonarchsDeckPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [current, setCurrent] = useState<SlideId>('cover');

  const idx  = SLIDES.findIndex(s => s.id === current);
  const prev = idx > 0 ? SLIDES[idx - 1].id : null;
  const next = idx < SLIDES.length - 1 ? SLIDES[idx + 1].id : null;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!unlocked) return;
      if (e.key === 'ArrowRight' && next) setCurrent(next);
      if (e.key === 'ArrowLeft'  && prev) setCurrent(prev);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [unlocked, next, prev]);

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  return (
    <div style={{
      minHeight: '100vh', background: '#050911',
      fontFamily: "'Barlow Condensed', 'Barlow', sans-serif",
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 0,
        background: '#0a101e', borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '0 24px', height: 48, flexShrink: 0,
        overflowX: 'auto',
      }}>
        <img
          src="/bthd-email-sig-300x80-transparent.png"
          alt="BTHD"
          style={{ height: 16, width: 'auto', opacity: 0.80, marginRight: 8, flexShrink: 0 }}
        />
        <div style={{
          width: 1, height: 20, background: 'rgba(255,255,255,0.1)',
          margin: '0 8px', flexShrink: 0,
        }} />
        <div style={{ marginRight: 18, flexShrink: 0 }}>
          <MonarchsLogo height={18} />
        </div>
        {SLIDES.map(s => (
          <button
            key={s.id}
            onClick={() => setCurrent(s.id)}
            style={{
              padding: '0 14px', height: '100%', background: 'none',
              border: 'none', borderBottom: `2px solid ${s.id === current ? ORG : 'transparent'}`,
              color: s.id === current ? '#fff' : 'rgba(255,255,255,0.4)',
              fontSize: 11, fontWeight: s.id === current ? 800 : 600,
              cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: 0.5,
              fontFamily: "'Barlow Condensed', sans-serif",
              transition: 'color 0.15s',
            }}
          >{s.label}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0, paddingLeft: 16 }}>
          <button
            onClick={() => prev && setCurrent(prev)} disabled={!prev}
            style={{
              padding: '4px 12px', background: SURFACE,
              border: `1px solid ${BORDER}`, borderRadius: 4,
              color: prev ? '#fff' : 'rgba(255,255,255,0.2)',
              fontSize: 12, cursor: prev ? 'pointer' : 'default',
              fontFamily: 'inherit',
            }}>←</button>
          <button
            onClick={() => next && setCurrent(next)} disabled={!next}
            style={{
              padding: '4px 12px', background: SURFACE,
              border: `1px solid ${BORDER}`, borderRadius: 4,
              color: next ? '#fff' : 'rgba(255,255,255,0.2)',
              fontSize: 12, cursor: next ? 'pointer' : 'default',
              fontFamily: 'inherit',
            }}>→</button>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '32px 48px' }}>
        <div style={{ maxWidth: 1100, width: '100%', margin: '0 auto' }}>
          <ScaledSlide>
            {renderSlide(current)}
          </ScaledSlide>
        </div>
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: 1 }}>
          {idx + 1} / {SLIDES.length}
        </div>
      </div>
    </div>
  );
}
